/**
 * @fileoverview Build 1-2, 1-3, and 1-4 atom-pair sets from a bond list.
 *
 * The engine's nonbonded inner loop uses these sets to:
 *   - **Exclude** 1-2 (directly bonded) and 1-3 (separated by one atom) pairs
 *     entirely from the nonbonded sum.
 *   - **Scale** 1-4 (separated by two atoms) pairs by AMBER's scee/scnb
 *     factors. Per `LigandParameters` (types.js), the engine consumes
 *     `exclusions` and `scaledPairs` as sorted packed `Int32Array`s.
 *
 * **Ring topology rule**: a pair that is both 1-3 (via one path) and 1-4
 * (via another, longer path) in a fused-ring system is treated as 1-3
 * (excluded), per AMBER convention. "Closer relationship wins."
 *
 * Indexing: all atom indices are local to the ligand (0 .. nAtoms-1) and
 * follow the same `atomNames`-positional convention used everywhere else
 * in the engine (see getTypedParameters.js).
 *
 * This module is primarily a fallback / test-fixture aid: when the server
 * supplies exclusions in `RawForcefieldData.exclusions`, the engine uses
 * those directly. When they're absent (legacy server, hand-built fixtures
 * in the finite-diff tests), this module derives them from the bond graph.
 *
 * See: getTypedParameters.js (`deriveScaledPairs` placeholder calls into this
 *      module's `buildExclusionSets`).
 */

/**
 * Packed (i,j) pair where i < j. Uses bit shift to fit in a 32-bit int —
 * safe for ligands up to 2^15 atoms (32768), which is well beyond any
 * realistic drug-like compound (typically <200 atoms).
 *
 * @param {number} a
 * @param {number} b
 * @returns {number}
 */
function packPair(a, b) {
    const i = a < b ? a : b;
    const j = a < b ? b : a;
    return (i << 16) | j;
}

/**
 * Build per-atom adjacency lists from a flat bond-index array.
 *
 * @param {Int32Array | number[]} bondPairs   Length 2*nBonds, flat (i,j) pairs.
 * @param {number} nAtoms
 * @returns {number[][]}                       adjacency[i] = list of neighbors of atom i.
 */
function buildAdjacency(bondPairs, nAtoms) {
    /** @type {number[][]} */
    const adj = new Array(nAtoms);
    for (let i = 0; i < nAtoms; i++) adj[i] = [];
    for (let b = 0; b < bondPairs.length; b += 2) {
        const i = bondPairs[b];
        const j = bondPairs[b + 1];
        if (i === j) continue; // defensive; shouldn't happen
        adj[i].push(j);
        adj[j].push(i);
    }
    return adj;
}

/**
 * Build 1-2, 1-3, 1-4 atom-pair sets from a bond list.
 *
 * Algorithm:
 *   1. 1-2 set = direct bonds (each (i,j) with i<j stored as packed pair).
 *   2. 1-3 set = 2-hop pairs (i -- k -- j with i!=j and j not in 1-2).
 *   3. 1-4 set = 3-hop pairs (i -- k -- l -- j with j not in 1-2 and not
 *      in 1-3). Ring topology rule applies: a pair classified as 1-3 by
 *      any path is permanently 1-3, even if other paths would make it
 *      1-4.
 *
 * Output sets are deduplicated and returned as sorted packed `Int32Array`s
 * — the form the engine's sentinel-pointer nonbonded loop walks.
 *
 * @param {Int32Array | number[]} bondPairs   Length 2*nBonds, flat (i,j) pairs.
 * @param {number} nAtoms
 * @returns {{
 *   exclusions: Int32Array,      // 1-2 union 1-3, sorted packed (i,j) pairs, length 2*N_excl.
 *   scaledPairs: Int32Array,     // 1-4 minus 1-2 minus 1-3, sorted packed
 *                                // (i,j) pairs, length 2*N_scaled.
 * }}
 */
export function buildExclusionSets(bondPairs, nAtoms) {
    const adj = buildAdjacency(bondPairs, nAtoms);

    /** @type {Set<number>} packed (i<<16)|j */
    const set12 = new Set();
    for (let b = 0; b < bondPairs.length; b += 2) {
        const i = bondPairs[b];
        const j = bondPairs[b + 1];
        if (i === j) continue;
        set12.add(packPair(i, j));
    }

    /** 1-3 set: for every atom k, every pair of its neighbors is a 1-3 pair. */
    const set13 = new Set();
    for (let k = 0; k < nAtoms; k++) {
        const nbrs = adj[k];
        for (let a = 0; a < nbrs.length; a++) {
            for (let b = a + 1; b < nbrs.length; b++) {
                const i = nbrs[a];
                const j = nbrs[b];
                if (i === j) continue;
                const p = packPair(i, j);
                if (set12.has(p)) continue; // ring: 1-2 dominates
                set13.add(p);
            }
        }
    }

    /**
     * 1-4 set: for every bond (k,l), every pair (i,j) where i is a neighbor
     * of k (i != l), j is a neighbor of l (j != k), and (i,j) is not 1-2
     * and not 1-3.
     */
    const set14 = new Set();
    for (let b = 0; b < bondPairs.length; b += 2) {
        const k = bondPairs[b];
        const l = bondPairs[b + 1];
        const kNbrs = adj[k];
        const lNbrs = adj[l];
        for (const i of kNbrs) {
            if (i === l) continue;
            for (const j of lNbrs) {
                if (j === k) continue;
                if (i === j) continue; // would be 1-3 via k-i-j-... etc., not a path
                const p = packPair(i, j);
                if (set12.has(p)) continue;
                if (set13.has(p)) continue; // ring topology: 1-3 wins
                set14.add(p);
            }
        }
    }

    // Build the exclusion list (1-2 union 1-3) and the scaled-pair list
    // (1-4 only). Sort ascending so the engine's sentinel-pointer walk
    // can advance pointers monotonically.
    const exclusionsPacked = [...set12, ...set13].sort((a, b) => a - b);
    const scaledPacked = [...set14].sort((a, b) => a - b);

    return {
        exclusions: unpackPairsToInt32Array(exclusionsPacked),
        scaledPairs: unpackPairsToInt32Array(scaledPacked),
    };
}

/**
 * Normalize an externally-supplied flat pair list (e.g. server-supplied
 * `RawForcefieldData.exclusions`) into the exact form the nonbonded
 * sentinel-pointer walk requires: each pair stored with i<j and the whole
 * list sorted ascending by the packed key `(i << 16) | j`. Duplicates
 * collapse (Set-backed), matching what `buildExclusionSets` produces.
 *
 * Reuses the same `packPair` (i<j) / sort / `unpackPairsToInt32Array`
 * pipeline as `buildExclusionSets`, so a wire-order `(j,i)` pair (j>i) is
 * canonicalized to `(i,j)` and slotted into sorted order rather than being
 * silently missed by the walk.
 *
 * @param {Int32Array | number[]} flatPairs   Length 2*N, flat (i,j) pairs, any order.
 * @returns {Int32Array}                       Sorted packed (i<j) pairs.
 */
export function normalizePackedPairs(flatPairs) {
    const packed = new Set();
    for (let p = 0; p + 1 < flatPairs.length; p += 2) {
        const i = flatPairs[p];
        const j = flatPairs[p + 1];
        if (i === j) continue; // self-pair: never a real exclusion
        packed.add(packPair(i, j));
    }
    return unpackPairsToInt32Array([...packed].sort((a, b) => a - b));
}

/**
 * Convert an array of packed pairs back to a flat Int32Array of (i, j, i, j, ...).
 * @param {number[]} packed
 * @returns {Int32Array}
 */
function unpackPairsToInt32Array(packed) {
    const out = new Int32Array(packed.length * 2);
    for (let p = 0; p < packed.length; p++) {
        const v = packed[p];
        out[2 * p] = (v >>> 16) & 0xFFFF;
        out[2 * p + 1] = v & 0xFFFF;
    }
    return out;
}
